import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Typography, Button, Box } from "@mui/material";
import { TextField } from "formik-mui";
import { useTranslation } from "react-i18next";
import AttachmentIcon from "@mui/icons-material/Attachment";
import Modal from "../../Modal/Modal";
import TextFieldUploadButton from "../../Buttons/TextFieldUploadButton";
import {
  repositoryFileMimeTypes,
  repositoryVideoMimeTypes,
  processVideoMimeTypes
} from "../../../constants";
import { fileUploadValidation } from "../../../utils/validation";
import ProcessFileModal from "../../ProcessFileModal/ProcessFileModal";
import ChipInput from "../../FormikFields/ChipInput";

const ProcessAssignmentText = ({ filename }) => {
  const { t } = useTranslation();
  return (
    <>
      {t("videoProcessing.processing")} {filename}
    </>
  );
};

const EditFileItemForm = ({
  submitForm,
  onClose,
  open,
  isSubmitting,
  resetForm,
  errors,
  acceptTypes,
  isScorm,
  processing
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    resetForm();
  }, [open]);

  const labelField = value => (
    <Typography variant="h6" color="textPrimary">
      {value}
    </Typography>
  );

  return (
    <Form>
      <Modal
        scrollable
        disableEnforceFocus
        showCloseIcon
        maxWidth="sm"
        fullWidth
        dialogTitle={
          <Typography variant="h4" color="textPrimary">
            {t("courseBuilder.editFileItemModalTitle")}
          </Typography>
        }
        dialogContent={
          <Box display="flex" flexDirection="column" mb={1}>
            <Box mb={3}>
              {processing && (
                <ProcessAssignmentText filename={processing.filename} />
              )}
              <Field
                variant="filled"
                fullWidth
                component={({ field: { value } }) => labelField(value)}
                name="fileName"
              />
            </Box>
            <Box mb={3}>
              <Field
                label="File"
                name="file"
                variant="filled"
                fullWidth
                component={({
                  form: { setFieldValue, errors, touched, submitCount },
                  field: { value }
                }) => (
                  <TextFieldUploadButton
                    onFileUpload={file => {
                      setFieldValue("file", file);
                      setFieldValue("fileName", file ? file.name : "");
                    }}
                    file={value}
                    disableUnderline={false}
                    icon={<AttachmentIcon />}
                    helperText={t("courseBuilder.uploadFileHelperText")}
                    acceptType={acceptTypes.join()}
                    error={
                      (touched.file || submitCount > 0) && errors.file
                        ? errors.file
                        : null
                    }
                  />
                )}
              />
            </Box>
            <Box mb={3}>
              <Field
                variant="filled"
                fullWidth
                label={t("courseBuilder.editActivityField.title")}
                component={TextField}
                name="title"
              />
            </Box>
            <Box mb={3}>
              <Field
                variant="filled"
                fullWidth
                label={t("courseBuilder.editActivityField.description")}
                component={TextField}
                multiline
                rows="4"
                name="description"
              />
            </Box>
            {!isScorm && (
              <Box mb={3}>
                <Field
                  variant="filled"
                  fullWidth
                  component={TextField}
                  name="courseTitle"
                  label={t("courseBuilder.editActivityField.courseTitle")}
                  disabled={true}
                />
              </Box>
            )}
            {!isScorm && (
              <Box mb={3}>
                <Field
                  variant="filled"
                  fullWidth
                  label={t("courseBuilder.editActivityField.tags")}
                  component={ChipInput}
                  name="tags"
                  inputProps={{ "data-qa": "edit-file-item-tags-field" }}
                />
              </Box>
            )}
            {!isScorm && (
              <Box mb={3}>
                <Field
                  variant="filled"
                  fullWidth
                  label={t("courseBuilder.editActivityField.comment")}
                  component={TextField}
                  multiline
                  rows="4"
                  name="comment"
                />
              </Box>
            )}
          </Box>
        }
        dialogActions={
          <>
            <Button color="primary" disabled={isSubmitting} onClick={onClose}>
              {t("cancelBtn")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={submitForm}
              disabled={isSubmitting}
            >
              {t("saveBtn")}
            </Button>
          </>
        }
        open={open}
        onClose={onClose}
      />
    </Form>
  );
};
const getEditFileItemSchema = (t, itemType) => {
  return Yup.object().shape({
    title: Yup.string()
      .required(t("validationMessages.titleRequired"))
      .max(
        255,
        t("validationMessages.cannotExceedLength", {
          type: t("courseBuilder.uploadFileTitleField"),
          maxLength: 255
        })
      ),
    file: fileUploadValidation(t, false, itemType),
    description: Yup.string().max(
      350,
      t("validationMessages.cannotExceedLength", {
        type: t("courseBuilder.uploadFileDescriptionField"),
        maxLength: 350
      })
    ),
    comment: Yup.string().max(
      350,
      t("validationMessages.cannotExceedLength", {
        type: t("courseBuilder.editActivityField.comment"),
        maxLength: 350
      })
    )
  });
};

const EditFileItemModal = ({
  item,
  onClose,
  open,
  onSaveActivity,
  isScorm
}) => {
  const { t } = useTranslation();
  const acceptTypes =
    item.type === "video"
      ? repositoryVideoMimeTypes
      : repositoryFileMimeTypes.filter(
          type => !repositoryVideoMimeTypes.includes(type)
        );
  const [processVideoModal, setProcessVideoModal] = useState();
  const checkProcessingSave = (values, { setSubmitting }) => {
    values && values.file && processVideoMimeTypes.includes(values.file.type)
      ? setProcessVideoModal(true)
      : onSaveActivity(values, error => {
          setSubmitting(false);
        });
  };
  return (
    <Formik
      initialValues={{
        file: null,
        fileName: item.fileName,
        title: item.name,
        description: item.description || "",
        courseTitle: isScorm ? "" : item.courseName,
        comment: "",
        tags: item.tags || ""
      }}
      validationSchema={getEditFileItemSchema(t, item.type)}
      onSubmit={checkProcessingSave}
    >
      {({
        submitForm,
        isSubmitting,
        values,
        resetForm,
        setFieldValue,
        errors,
        setSubmitting
      }) => (
        <>
          <EditFileItemForm
            processing={item.processing}
            submitForm={submitForm}
            isSubmitting={isSubmitting}
            values={values}
            resetForm={resetForm}
            open={open}
            onClose={onClose}
            setFieldValue={setFieldValue}
            acceptTypes={acceptTypes}
            errors={errors}
            isScorm={isScorm}
          />
          <ProcessFileModal
            uploadVideo={() => {
              onSaveActivity(values, error => {
                setSubmitting(false);
                console.log(error);
              });
              setProcessVideoModal(false);
            }}
            onClose={() => {
              setProcessVideoModal(false);
              setSubmitting(false);
            }}
            open={processVideoModal}
          />
        </>
      )}
    </Formik>
  );
};

EditFileItemModal.propTypes = {
  item: PropTypes.object,
  /**
   * Called to close the Modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the form is submitted to upload the new file
   */
  onSave: PropTypes.func
};

export default EditFileItemModal;
